import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useState } from "react";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const filter = createFilterOptions();

const TIME_OPTIONS = [
  { time: "12:00 am" },
  { time: "12:15 am" },
  { time: "12:30 am" },
  { time: "12:45 am" },
  { time: "01:00 am" },
  { time: "01:15 am" },
  { time: "01:30 am" },
  { time: "01:45 am" },
  { time: "02:00 am" },
  { time: "02:15 am" },
  { time: "02:30 am" },
  { time: "02:45 am" },
  { time: "03:00 am" },
  { time: "03:15 am" },
  { time: "03:30 am" },
  { time: "03:45 am" },
  { time: "04:00 am" },
  { time: "04:15 am" },
  { time: "04:30 am" },
  { time: "04:45 am" },
  { time: "05:00 am" },
  { time: "05:15 am" },
  { time: "05:30 am" },
  { time: "05:45 am" },
  { time: "06:00 am" },
  { time: "06:15 am" },
  { time: "06:30 am" },
  { time: "06:45 am" },
  { time: "07:00 am" },
  { time: "07:15 am" },
  { time: "07:30 am" },
  { time: "07:45 am" },
  { time: "08:00 am" },
  { time: "08:15 am" },
  { time: "08:30 am" },
  { time: "08:45 am" },
  { time: "09:00 am" },
  { time: "09:15 am" },
  { time: "09:30 am" },
  { time: "09:45 am" },
  { time: "10:00 am" },
  { time: "10:15 am" },
  { time: "10:30 am" },
  { time: "10:45 am" },
  { time: "11:00 am" },
  { time: "11:15 am" },
  { time: "11:30 am" },
  { time: "11:45 am" },
  { time: "12:00 pm" },
  { time: "12:15 pm" },
  { time: "12:30 pm" },
  { time: "12:45 pm" },
  { time: "01:00 pm" },
  { time: "01:15 pm" },
  { time: "01:30 pm" },
  { time: "01:45 pm" },
  { time: "02:00 pm" },
  { time: "02:15 pm" },
  { time: "02:30 pm" },
  { time: "02:45 pm" },
  { time: "03:00 pm" },
  { time: "03:15 pm" },
  { time: "03:30 pm" },
  { time: "03:45 pm" },
  { time: "04:00 pm" },
  { time: "04:15 pm" },
  { time: "04:30 pm" },
  { time: "04:45 pm" },
  { time: "05:00 pm" },
  { time: "05:15 pm" },
  { time: "05:30 pm" },
  { time: "05:45 pm" },
  { time: "06:00 pm" },
  { time: "06:15 pm" },
  { time: "06:30 pm" },
  { time: "06:45 pm" },
  { time: "07:00 pm" },
  { time: "07:15 pm" },
  { time: "07:30 pm" },
  { time: "07:45 pm" },
  { time: "08:00 pm" },
  { time: "08:15 pm" },
  { time: "08:30 pm" },
  { time: "08:45 pm" },
  { time: "09:00 pm" },
  { time: "09:15 pm" },
  { time: "09:30 pm" },
  { time: "09:45 pm" },
  { time: "10:00 pm" },
  { time: "10:15 pm" },
  { time: "10:30 pm" },
  { time: "10:45 pm" },
  { time: "11:00 pm" },
  { time: "11:15 pm" },
  { time: "11:30 pm" },
  { time: "11:45 pm" },
];

// 15 minute increments
const SelectTime = ({
  timezone = DEFAULT_TIMEZONE,
  value,
  minTime,
  maxTime,
  label = "Time",
  onUpdate,
  disabled,
  onBlur,
  error,
  helperText,
  ...props
}) => {
  let optionsSubset = [...TIME_OPTIONS];
  if (minTime) {
    let timeZoneMinTime = minTime.tz(timezone).format("hh:mm a");
    // console.log("__minTime", timeZoneMinTime);
    let minIndex = optionsSubset.findIndex(
      (opt) => opt.time == timeZoneMinTime
    );
    // console.log("__AT minTime Index", optionsSubset[minIndex]);
    // console.log("__minTime Index", minIndex);
    // Slice to the left of minIndex
    // findIndex returns -1 if not found
    if (minIndex > -1 && minIndex > 0) {
      optionsSubset = optionsSubset.slice(minIndex); // if no start index, slice everything to the left of index
    }
  }
  if (maxTime) {
    let timeZoneMaxTime = maxTime.tz(timezone).format("hh:mm a");
    let maxIndex = optionsSubset.findIndex(
      (opt) => opt.time == timeZoneMaxTime
    );
    // Slice to the right of maxIndex
    // findIndex returns -1 if not found
    if (maxIndex > -1) {
      optionsSubset = optionsSubset.slice(0, maxIndex + 1);
    }
  }

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [times, setTimes] = useState(optionsSubset);

  return (
    <>
      <Autocomplete
        value={value}
        disabled={disabled}
        handleHomeEndKeys
        onChange={(event, newValue) => {
          if (onUpdate) {
            if (newValue) {
              let currentValueDate = value.tz(timezone).format("MM/DD/YYYY");
              let currentValueOffset = value.format("Z");
              let newTime = dayjs(
                // MM/DD/YYYY hh:mm Z
                `${currentValueDate} ${newValue.time} ${currentValueOffset}`
              );
              onUpdate(newTime ?? null);
            }
          }
        }}
        filterOptions={(options, params) => {
          // let optionsSubset = [...options];
          // if (minTime) {
          //   let timeZoneMinTime = minTime.tz(timezone).format("hh:mm a");
          //   let minIndex = options.findIndex(
          //     (opt) => opt.time == timeZoneMinTime
          //   );
          //   // Slice to the left of minIndex
          //   // findIndex returns -1 if not found
          //   if (minIndex > -1 && minIndex > 0) {
          //     optionsSubset = optionsSubset.slice(0, minIndex - 1);
          //   }
          // }
          // if (maxTime) {
          //   let timeZoneMaxTime = maxTime.tz(timezone).format("hh:mm a");
          //   let maxIndex = options.findIndex(
          //     (opt) => opt.time == timeZoneMaxTime
          //   );
          //   // Slice to the right of maxIndex
          //   // findIndex returns -1 if not found
          //   if (maxIndex > -1 && maxIndex < optionsSubset.length - 1) {
          //     optionsSubset = optionsSubset.slice(
          //       maxIndex + 1,
          //       optionsSubset.length - 1
          //     );
          //   }
          // }

          // console.log("subset", optionsSubset);

          const filtered = filter(optionsSubset, params);

          return filtered;
        }}
        options={times}
        getOptionLabel={(option) => {
          // if (typeof option === "string") {
          //   console.log("getOptionLabel(option) STRING OPTION", option);
          //   return option;
          // } else if (option.inputValue) {
          //   console.log(
          //     "getOptionLabel(option) INPUT VALUE",
          //     option.inputValue
          //   );
          //   // return option.inputValue;
          //   let timezoneValue = dayjs.tz(option, timezone).format("hh:mm a");
          //   return timezoneValue;
          // } else {
          //   console.log("getOptionLabel(option) ELSE OPTION", option);
          //   return option.time;
          // }

          // If option is an option, return time
          if (option.time) {
            return option.time;
          } else {
            // If option is the value, then parse the value
            if (option.tz) {
              let timezoneValue = option.tz(timezone).format("hh:mm a");
              return timezoneValue;
            }
          }
        }}
        selectOnFocus
        clearOnBlur
        isOptionEqualToValue={(option, value) => {
          //  The "selected" prop on renderOption depends on this
          if (value) {
            let timezoneValue = value.tz(timezone).format("hh:mm a");
            return timezoneValue == option.time;
          } else {
            return false;
          }
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;

          return (
            <li key={key} {...optionProps}>
              {option.time}
            </li>
          );
        }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            size={props.size ? props.size : "small"}
            label={label}
            sx={{
              width: "100%",
            }}
            variant="outlined" // filled, outlined, standard
            onBlur={onBlur}
            error={error}
            helperText={helperText}
            inputProps={{
              ...params.inputProps,
              // Disable pressing Enter
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                }
              },
            }}
          />
        )}
      />
    </>
  );
};

export default SelectTime;
